import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import messagesService from '../../services/messages';

function conversationUserId(conv) {
  const u = conv?.user;
  return u?._id ?? u?.id ?? null;
}

function normalizeSentMessage(res) {
  const body = res?.data?.data ?? res?.data ?? res;
  if (!body || typeof body !== 'object') return null;
  if (body.message && typeof body.message === 'object') return body.message;
  if (body._id || body.id) return body;
  return null;
}

/** API: { data: { conversations, nextCursor } } or legacy array */
function normalizeConversationsResponse(res) {
  const inner = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(inner)) {
    return { conversations: inner, nextCursor: null };
  }
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner.conversations)) {
      return {
        conversations: inner.conversations,
        nextCursor: inner.nextCursor ?? null,
      };
    }
    if (Array.isArray(inner.data)) {
      return {
        conversations: inner.data,
        nextCursor: inner.nextCursor ?? null,
      };
    }
  }
  return { conversations: [], nextCursor: null };
}

function bumpConversation(state, userId, lastMessage) {
  const idx = state.conversations.findIndex(
    (c) => String(conversationUserId(c)) === String(userId),
  );
  if (idx < 0) return;
  const conv = state.conversations[idx];
  conv.lastMessage = lastMessage;
  conv.unreadCount = 0;
  state.conversations.splice(idx, 1);
  state.conversations.unshift(conv);
}

function syncConversationPreviewFromThread(state, userId) {
  const msgs = state.threadMessages.filter((m) => !m.pending);
  const last = msgs[msgs.length - 1];
  if (!last) return;
  bumpConversation(state, userId, {
    content: last.content || (last.attachment ? '📎' : ''),
    attachment: last.attachment,
    createdAt: last.createdAt || new Date().toISOString(),
  });
}

const initialState = {
  loading: false,
  conversations: [],
  conversationsNextCursor: null,
  error: '',
  selectedUserId: null,
  threadLoading: false,
  threadMessages: [],
  threadError: '',
  threadErrorCode: null,
  sending: false,
  deletingId: null,
};

export const fetchConversations = createAsyncThunk(
  'adminMessages/fetchConversations',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await messagesService.getAllConversations(params);
      return normalizeConversationsResponse(res);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        return rejectWithValue({
          code: 'FORBIDDEN',
          message: e?.response?.data?.message,
        });
      }
      return rejectWithValue({
        code: 'ERROR',
        message: e?.message || 'Failed to load conversations',
      });
    }
  },
);

export const fetchThread = createAsyncThunk(
  'adminMessages/fetchThread',
  async (userId, { rejectWithValue }) => {
    if (!userId) {
      return { userId: null, messages: [] };
    }
    try {
      const res = await messagesService.getThread(userId);
      const msgs =
        res?.data?.data?.messages ?? res?.data?.messages ?? [];
      return {
        userId,
        messages: Array.isArray(msgs) ? msgs : [],
      };
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      const errCode = body?.error_code ?? body?.errorCode;
      if (status === 422 && errCode === 'FIREBASE_UID_NOT_LINKED') {
        return rejectWithValue({
          code: 'FIREBASE_NOT_LINKED',
          message: body?.message,
        });
      }
      if (status === 403) {
        return rejectWithValue({
          code: 'FORBIDDEN',
          message: body?.message,
        });
      }
      return rejectWithValue({
        code: 'ERROR',
        message: body?.message || e?.message || 'Failed to load thread',
      });
    }
  },
);

export const sendAdminMessage = createAsyncThunk(
  'adminMessages/send',
  async ({ formData, tempId }, { rejectWithValue }) => {
    try {
      const res = await messagesService.sendAdmin(formData);
      return { tempId, message: normalizeSentMessage(res) };
    } catch (e) {
      return rejectWithValue({
        tempId,
        message:
          e?.response?.data?.message || e?.message || 'Send failed',
      });
    }
  },
);

export const deleteAdminMessage = createAsyncThunk(
  'adminMessages/delete',
  async ({ ticketId, userId }, { rejectWithValue }) => {
    try {
      await messagesService.deleteTicket(ticketId);
      return { ticketId, userId };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message || e?.message || 'Delete failed',
      );
    }
  },
);

const adminMessagesSlice = createSlice({
  name: 'adminMessages',
  initialState,
  reducers: {
    selectConversation(state, action) {
      const c = action.payload;
      const u = c?.user;
      state.selectedUserId = u?._id ?? u?.id ?? null;
      state.threadMessages = [];
      state.threadError = '';
      state.threadErrorCode = null;
      const idx = state.conversations.findIndex(
        (item) =>
          String(conversationUserId(item)) === String(state.selectedUserId),
      );
      if (idx >= 0) {
        state.conversations[idx].unreadCount = 0;
      }
    },
    clearThread(state) {
      state.selectedUserId = null;
      state.threadMessages = [];
      state.threadError = '';
      state.threadErrorCode = null;
    },
    appendOptimisticMessage(state, action) {
      const { tempId, userId, content, hasAttachment } = action.payload;
      const now = new Date().toISOString();
      state.threadMessages.push({
        _id: tempId,
        content,
        fromAdmin: true,
        createdAt: now,
        pending: true,
      });
      bumpConversation(state, userId, {
        content: content || (hasAttachment ? '📎' : ''),
        createdAt: now,
      });
    },
    removeOptimisticMessage(state, action) {
      const tempId = action.payload;
      state.threadMessages = state.threadMessages.filter(
        (m) => m._id !== tempId,
      );
      if (state.selectedUserId) {
        syncConversationPreviewFromThread(state, state.selectedUserId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.conversations;
        state.conversationsNextCursor = action.payload.nextCursor ?? null;
        state.error = '';
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.conversations = [];
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load';
      })
      .addCase(fetchThread.pending, (state) => {
        state.threadLoading = true;
        state.threadError = '';
        state.threadErrorCode = null;
      })
      .addCase(fetchThread.fulfilled, (state, action) => {
        state.threadLoading = false;
        state.threadMessages = action.payload.messages;
        state.threadError = '';
        state.threadErrorCode = null;
        const uid = action.payload.userId;
        if (uid) {
          const idx = state.conversations.findIndex(
            (c) => String(conversationUserId(c)) === String(uid),
          );
          if (idx >= 0) {
            state.conversations[idx].unreadCount = 0;
          }
        }
      })
      .addCase(fetchThread.rejected, (state, action) => {
        state.threadLoading = false;
        state.threadMessages = [];
        const p = action.payload;
        if (p?.code === 'FIREBASE_NOT_LINKED') {
          state.threadErrorCode = 'FIREBASE_NOT_LINKED';
          state.threadError = '';
        } else {
          state.threadErrorCode = null;
          state.threadError =
            p?.message ||
            action.error?.message ||
            'Failed to load thread';
        }
      })
      .addCase(sendAdminMessage.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendAdminMessage.fulfilled, (state, action) => {
        state.sending = false;
        const { tempId, message } = action.payload;
        const idx = state.threadMessages.findIndex((m) => m._id === tempId);
        if (message) {
          const normalized = { ...message, fromAdmin: true, pending: false };
          if (idx >= 0) {
            state.threadMessages[idx] = normalized;
          } else {
            state.threadMessages.push(normalized);
          }
        } else if (idx >= 0) {
          state.threadMessages[idx] = {
            ...state.threadMessages[idx],
            pending: false,
          };
        }
        if (state.selectedUserId) {
          syncConversationPreviewFromThread(state, state.selectedUserId);
        }
      })
      .addCase(sendAdminMessage.rejected, (state, action) => {
        state.sending = false;
        const tempId = action.payload?.tempId ?? action.meta.arg?.tempId;
        if (tempId) {
          state.threadMessages = state.threadMessages.filter(
            (m) => m._id !== tempId,
          );
          if (state.selectedUserId) {
            syncConversationPreviewFromThread(
              state,
              state.selectedUserId,
            );
          }
        }
      })
      .addCase(deleteAdminMessage.pending, (state, action) => {
        state.deletingId = action.meta.arg?.ticketId ?? null;
        const ticketId = action.meta.arg?.ticketId;
        state.threadMessages = state.threadMessages.filter(
          (m) => m._id !== ticketId,
        );
      })
      .addCase(deleteAdminMessage.fulfilled, (state, action) => {
        state.deletingId = null;
        const userId = action.payload?.userId;
        if (userId) {
          syncConversationPreviewFromThread(state, userId);
        }
      })
      .addCase(deleteAdminMessage.rejected, (state) => {
        state.deletingId = null;
      });
  },
});

export const {
  selectConversation,
  clearThread,
  appendOptimisticMessage,
  removeOptimisticMessage,
} = adminMessagesSlice.actions;
export default adminMessagesSlice.reducer;
